import CheckMarkIcon from '@atlaskit/icon/core/check-mark';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { messages } from '@databases/intl/messages';
import Z_INDEX from '@databases/layout/z-index';
import { type SelectOptionColor } from '@databases/model/field';
import { type SelectOption } from '@databases/types/database';
import React, {
  type FunctionComponent,
  forwardRef,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl-next';
import styled, { css } from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import {
  type ColorPalette,
  TagColorPalette,
  TagColorPaletteADSRefresh,
  PageStatusPalette,
} from './palettes';
import { paletteColorsMap } from './palettes/types';

const OptionsContainer = styled.div<{ size: number; length: number }>`
  display: grid;
  grid-template-columns: repeat(
    ${(props) => Math.ceil(props.length / 2)},
    ${(props) => props.size}px
  );
  grid-template-rows: 1fr 1fr;
  gap: ${token('space.050', '4px')};
  padding: ${token('space.050', '4px')};
`;

const NewOptionsContainer = styled.div<{ size: number; length: number }>`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: ${token('space.050', '4px')};
  padding: ${token('space.050', '4px')};
`;

export interface ColorPickerProps<T = any> {
  palette: ColorPalette<T>;
  value: T;
  onChange: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;

  /** The width/height of the picker square in pixels. Defaults to 30. */
  size?: number;

  /** If true, stop the propagation of click events. */
  stopPropagation?: boolean;
}

export const ColorPickerPicker: React.FC<
  Pick<Required<ColorPickerProps>, 'onChange' | 'palette' | 'size' | 'value'> & {
    setIsOpen: (isOpen: boolean) => void;
  } & Pick<ColorPickerProps, 'stopPropagation'>
> = ({ onChange, palette, size, value, setIsOpen, stopPropagation }) => {
  const paletteNames = useMemo(() => palette.swatch, [palette]);
  const { formatMessage } = useIntl();

  const handleClick = useCallback(
    (color: string) => {
      setIsOpen(false);
      onChange(color);
    },
    [onChange, setIsOpen]
  );

  return (
    <OptionsContainer
      size={size}
      length={paletteNames.length}
      role="radiogroup"
      aria-label={formatMessage(messages['color-picker.options-container'])}
    >
      {paletteNames.map((color) => (
        <Tooltip
          key={color}
          content={formatMessage(paletteColorsMap[color])}
          testId="color-option-tooltip"
        >
          <ColorPickerOption
            color={color}
            palette={palette}
            isChecked={color === value}
            onClick={() => handleClick(color)}
            size={size}
            stopPropagation={stopPropagation}
          />
        </Tooltip>
      ))}
    </OptionsContainer>
  );
};

export const NewColorPickerPicker: React.FC<
  Pick<Required<ColorPickerProps>, 'onChange' | 'palette' | 'size' | 'value'> & {
    setIsOpen: (isOpen: boolean) => void;
  } & Pick<ColorPickerProps, 'stopPropagation'>
> = ({ onChange, palette, size, value, setIsOpen, stopPropagation }) => {
  const paletteNames = useMemo(() => palette.swatch, [palette]);
  const { formatMessage } = useIntl();

  const handleClick = useCallback(
    (color: string) => {
      setIsOpen(false);
      onChange(color);
    },
    [onChange, setIsOpen]
  );

  return (
    <NewOptionsContainer
      size={size}
      length={paletteNames.length}
      role="radiogroup"
      aria-label={formatMessage(messages['color-picker.options-container'])}
    >
      {paletteNames.map((color) => (
        <Tooltip
          key={color}
          content={formatMessage(paletteColorsMap[color])}
          testId="color-option-tooltip"
        >
          <ColorPickerOption
            color={color}
            palette={palette}
            isChecked={color === value}
            onClick={() => handleClick(color)}
            size={size}
            stopPropagation={stopPropagation}
          />
        </Tooltip>
      ))}
    </NewOptionsContainer>
  );
};

/**
 * Renders a single option of an Databases select field, styled as an @atlaskit/tag component.
 */
const ColorPicker: FunctionComponent<ColorPickerProps> = ({
  palette,
  value,
  onChange,
  onFocus,
  onBlur,
  stopPropagation,
  size = 30,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const useNewColorPicker = isFeatureFlagEnabled('platform-component-visual-refresh');
  const PickerComponent = useMemo(
    () => (useNewColorPicker ? NewColorPickerPicker : ColorPickerPicker),
    [useNewColorPicker]
  );

  return (
    <Popup
      onClose={handleClose}
      isOpen={isOpen}
      content={() => (
        <PickerComponent
          onChange={onChange}
          palette={palette}
          setIsOpen={setIsOpen}
          size={size}
          value={value}
          stopPropagation={stopPropagation}
        />
      )}
      trigger={(triggerProps) => (
        <ColorPickerOption
          {...triggerProps}
          color={value}
          palette={palette}
          onClick={handleToggle}
          onFocus={onFocus}
          onBlur={onBlur}
          size={size}
          stopPropagation={stopPropagation}
          isOpen={isOpen}
        />
      )}
      placement="bottom-start"
      zIndex={Z_INDEX.COLOR_PICKER}
      autoFocus={!stopPropagation}
    />
  );
};

type ColorPickerOptionContainerProps = {
  size: number;
  isClickable?: boolean;
  color: SelectOptionColor;
  palette: ColorPalette;
};

const ColorPickerOptionContainer = styled.button<ColorPickerOptionContainerProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
  flex-shrink: 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'inherit')};
  border: none;
  color: ${(props: ColorPickerOptionContainerProps) =>
    props.palette.textColor(props.color)};
  ${(props) =>
    props.palette.borderColor &&
    `
      outline: ${`1px solid ${props.palette.borderColor(props.color)}`};
      outline-offset: -1px;
    `}
  background-color: ${(props: ColorPickerOptionContainerProps) =>
    props.palette.backgroundColor(props.color)};
  border-radius: 3px;

  &:focus-visible {
    outline: 2px solid ${token('color.border.focused', '#388BFF')};
    outline-offset: 2px;
  }

  &:hover {
    ${(props: ColorPickerOptionContainerProps) =>
      props.palette.hoverBackgroundColor
        ? css`
            background-color: ${props.palette.hoverBackgroundColor(props.color)};
          `
        : css`
            filter: brightness(130%);
          `}
  }
`;

const NewColorPickerOptionContainer = styled.button<ColorPickerOptionContainerProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: ${(props) => props.size}px;
  width: ${(props) => props.size}px;
  flex-shrink: 0;
  cursor: ${(props) => (props.isClickable ? 'pointer' : 'inherit')};
  border: none;
  color: ${(props: ColorPickerOptionContainerProps) =>
    props.palette.textColor(props.color)};
  ${(props: ColorPickerOptionContainerProps) =>
    props.palette.borderColor
      ? `border: 1px solid ${props.palette.borderColor(props.color)}`
      : ''};
  background-color: ${(props: ColorPickerOptionContainerProps) =>
    props.palette.backgroundColor(props.color)};
  border-radius: 3px;

  &:focus-visible {
    outline: 2px solid ${token('color.border.focused', '#388BFF')};
    outline-offset: 2px;
  }

  &:hover {
    outline: 1px solid ${token('color.border')};
    outline-offset: 1px;
  }
`;

export interface ColorPickerOptionProps<T = string> {
  color: T;
  palette: ColorPalette<T>;
  onClick?: () => void;
  isChecked?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  size?: number;
  stopPropagation?: boolean;
  isOpen?: boolean;
}

const ColorPickerOption = forwardRef<HTMLSpanElement, ColorPickerOptionProps>(
  function ColorPickerOption(
    {
      color,
      palette,
      onClick,
      isChecked,
      isOpen,
      onFocus,
      onBlur,
      stopPropagation,
      size = 30,
    },
    ref
  ) {
    const { formatMessage } = useIntl();
    const handleClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        if (stopPropagation) {
          e.stopPropagation();
        }
        onClick?.();
      },
      [onClick, stopPropagation]
    );

    const useNewColorPicker = isFeatureFlagEnabled('platform-component-visual-refresh');
    const ColorPickerOptionContainerComponent = useMemo(
      () =>
        useNewColorPicker ? NewColorPickerOptionContainer : ColorPickerOptionContainer,
      [useNewColorPicker]
    );

    return (
      <ColorPickerOptionContainerComponent
        id="color-picker-option-container"
        onClick={handleClick}
        onFocus={onFocus}
        onBlur={onBlur}
        size={size}
        isClickable={!!onClick}
        color={color}
        ref={ref}
        tabIndex={0}
        role="radio"
        aria-checked={isChecked || false}
        aria-expanded={isOpen}
        aria-label={color}
        palette={palette}
      >
        {isChecked && (
          <CheckMarkIcon
            spacing="spacious"
            label={formatMessage(messages['color-picker.selected-label'])}
          />
        )}
      </ColorPickerOptionContainerComponent>
    );
  }
);

export interface SelectColorPickerProps
  extends Omit<ColorPickerProps, 'palette' | 'value' | 'onChange'> {
  value: SelectOption['color'];
  onChange: (value: SelectOption['color']) => void;
}

export const SelectColorPicker: FunctionComponent<SelectColorPickerProps> = ({
  value,
  onChange,
  ...props
}) => {
  const handleChange = useCallback(
    (value: string) => {
      if (value) {
        onChange(value);
      }
    },
    [onChange]
  );

  const useADSRefreshPalette = isFeatureFlagEnabled('platform-component-visual-refresh');

  return (
    <ColorPicker
      {...props}
      palette={useADSRefreshPalette ? TagColorPaletteADSRefresh : TagColorPalette}
      value={value}
      onChange={handleChange}
    />
  );
};

export const PageStatusColorPicker: FunctionComponent<
  Omit<ColorPickerProps, 'palette'>
> = (props) => <ColorPicker palette={PageStatusPalette} {...props} />;
